import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { siteConfig } from "../siteConfig";
import Reveal from "../components/Reveal";

const toSlug = (area) => area.toLowerCase().replace(/\s+/g, "-");

const mapEmbedFor = (area) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(`${area}, ${siteConfig.state}`)}&output=embed`;

const ServiceAreasPage = () => (
  <>
    {/* ── PAGE HERO ── */}
    <section
      className="relative flex items-end"
      style={{
        minHeight: "38vh",
        backgroundImage: `url(${siteConfig.hero.image})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 100%)" }} />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pb-12 pt-32">
        <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: siteConfig.colors.primary }}>
          Where We Work
        </p>
        <h1 className="font-display font-black text-white leading-tight mb-3" style={{ fontSize: "clamp(2.2rem,5vw,3.75rem)" }}>
          Service Areas
        </h1>
        <p className="text-white/70 text-lg max-w-2xl">
          We proudly serve {siteConfig.city} and the surrounding communities throughout {siteConfig.state}.
        </p>
      </div>
    </section>

    {/* ── MAP + AREA LIST ── */}
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-[3fr_2fr] gap-10 items-start">

          {/* Left — map centered on the first service area */}
          <Reveal as="div" className="rounded-2xl overflow-hidden border border-gray-200 shadow-md w-full" style={{ height: "420px" }}>
            <iframe
              src={siteConfig.mapEmbedUrl || mapEmbedFor(siteConfig.serviceAreas[0])}
              title={`Map of ${siteConfig.serviceAreas[0]}, ${siteConfig.state}`}
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>

          {/* Right — area list */}
          <Reveal as="div" animation="right" className="lg:pt-2">
            <h2
              className="font-display font-black leading-tight mb-8"
              style={{ fontSize: "clamp(1.75rem,3.5vw,2.75rem)", color: siteConfig.colors.dark }}
            >
              Areas We Serve
            </h2>

            <ul className="space-y-1">
              {siteConfig.serviceAreas.map((area) => (
                <li key={area}>
                  <Link
                    to={`/service-areas/${toSlug(area)}`}
                    className="group flex items-center justify-between py-3 border-b border-gray-100 transition-colors hover:border-current"
                    style={{ "--hover-color": siteConfig.colors.primary }}
                  >
                    <span
                      className="font-semibold text-base underline underline-offset-4 decoration-gray-300 group-hover:decoration-current transition-colors"
                      style={{ color: siteConfig.colors.dark }}
                    >
                      {area}, {siteConfig.state}
                    </span>
                    <ArrowRight
                      className="w-4 h-4 shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150"
                      style={{ color: siteConfig.colors.primary }}
                    />
                  </Link>
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 px-6 h-12 text-sm font-bold text-white rounded-xl transition-opacity hover:opacity-90"
              style={{ backgroundColor: siteConfig.colors.primary }}
            >
              Get a Free Estimate
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    {/* ── AREA CARDS GRID ── */}
    <section className="pb-20 md:pb-28" style={{ backgroundColor: siteConfig.colors.light }}>
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <Reveal as="div" className="pt-14 mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: siteConfig.colors.primary }}>
            Browse by Location
          </p>
          <h2
            className="font-display font-black leading-tight"
            style={{ fontSize: "clamp(1.5rem,3vw,2.25rem)", color: siteConfig.colors.dark }}
          >
            Pick your area for local details
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {siteConfig.serviceAreas.map((area, i) => (
            <Reveal
              as={Link}
              key={area}
              delay={(i % 6) * 70}
              to={`/service-areas/${toSlug(area)}`}
              className="group flex items-center gap-4 p-5 rounded-xl bg-white border border-gray-200 hover:border-transparent hover:shadow-lg transition-all duration-200"
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200"
                style={{ backgroundColor: `${siteConfig.colors.primary}18` }}
              >
                <MapPin
                  className="w-5 h-5"
                  style={{ color: siteConfig.colors.primary }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="font-bold text-sm truncate"
                  style={{ color: siteConfig.colors.dark }}
                >
                  {area}
                </div>
                <div className="text-xs text-gray-500">{siteConfig.state}</div>
              </div>
              <ArrowRight
                className="w-4 h-4 shrink-0 text-gray-300 group-hover:translate-x-1 group-hover:text-current transition-all duration-150"
                style={{ "--tw-text-opacity": 1 }}
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>

  </>
);

export default ServiceAreasPage;
